import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import classNames from 'classnames';
import { defineMessages, injectIntl } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import { Link } from 'react-router-dom';

import { xboxAvailable } from 'Helpers/Geo/data';
import AccessibilityMenu from 'Layout/NavBar/MegaMenu/AccessibilityMenu';
import ProductsMegaMenuTabLink from 'Layout/NavBar/MegaMenu/ProductsMegaMenuTabLink';
import ProductsMegaMenuTab from 'Layout/NavBar/MegaMenu/ProductsMegaMenuTab';
import StarDealTab from 'Layout/NavBar/MegaMenu/StarDealTab';
import FeaturedTab from 'Layout/NavBar/MegaMenu/FeaturedTab';
import { logViewed } from 'Api/Recommendations/user-recommendations';

const messages = defineMessages({
  topsellers: {
    id: 'nav.store-menu.topsellers',
    defaultMessage: 'Top Sellers',
  },
  topsellerstext: {
    id: 'nav.store-menu.topsellerstext',
    defaultMessage: 'Are you searching for the hottest sales on the site? Look no further than Top Sellers! You can find all the hottest offers on games, bundles, eBooks and software.',
  },
  gamedeals: {
    id: 'nav.store-menu.gamedeals',
    defaultMessage: 'All Game Deals',
  },
  gamedealstext: {
    id: 'nav.store-menu.gamedealstext',
    defaultMessage: 'Don’t miss out on some of the best and most popular deals on sale right now at Fanatical. You can find top selling PC games and bundles from official partners.',
  },
  latestdeals: {
    id: 'nav.store-menu.latestdeals',
    defaultMessage: 'Latest Deals',
  },
  latestdealstext: {
    id: 'nav.store-menu.latestdealstext',
    defaultMessage: 'If you’re looking for the latest and greatest deals right now, we\'re sure you’ll find a fantastic bargain on our site. Check out our latest PC game discounts here.',
  },
  trendingdeals: {
    id: 'nav.store-menu.trendingdeals',
    defaultMessage: 'Trending Deals',
  },
  trendingdealstext: {
    id: 'nav.store-menu.trendingdealstext',
    defaultMessage: 'The games everyone is talking about, from brand new releases to long-time favourites!',
  },
  newreleases: {
    id: 'nav.store-menu.newreleases',
    defaultMessage: 'New Releases',
  },
  newreleasestext: {
    id: 'nav.store-menu.newreleasestext',
    defaultMessage: 'If you’re looking for the latest new game releases you’ve come to the right place! Here you’ll find all the newest chart-topping games, including our best sellers.',
  },
  upcominggames: {
    id: 'nav.store-menu.upcominggames',
    defaultMessage: 'Upcoming Games',
  },
  upcominggamestext: {
    id: 'nav.store-menu.upcominggamestext',
    defaultMessage: 'Find all upcoming games and grab some sweet bonuses when you pre-order. Pre-order or wishlist for later so you don’t miss out on release day.',
  },
  lastchance: {
    id: 'nav.store-menu.lastchance',
    defaultMessage: 'Last Chance to Save',
  },
  lastchancetext: {
    id: 'nav.store-menu.lastchancetext',
    defaultMessage: 'Time is running out on these deals. Don\'t miss out on your chance to save big and grab a bargain.',
  },
  gamebundles: {
    id: 'nav.store-menu.gamebundles',
    defaultMessage: 'Game Bundles',
  },
  gamebundlestext: {
    id: 'nav.store-menu.gamebundlestext',
    defaultMessage: 'Fanatical’s game bundles are exclusive, limited-time collections of games at great prices.',
  },
  stardeal: {
    id: 'nav.store-menu.stardeal',
    defaultMessage: 'Star Deal',
  },
  bestgamesof2025: {
    id: 'nav.store-menu.bestgamesof2025',
    defaultMessage: 'Best Games of 2025',
  },
  bestgamesof2025text: {
    id: 'nav.store-menu.bestgamesof2025text',
    defaultMessage: 'The best game releases in 2025 rated by Metacritic, featuring numerous Game of the Year winners',
  },
  browseAll: {
    id: 'nav.store-menu.browseAll',
    defaultMessage: 'Browse All',
  },
  freeToPlay: {
    id: 'nav.store-menu.freeToPlay',
    defaultMessage: 'Free to Play',
  },
  freeToPlayText: {
    id: 'nav.store-menu.freeToPlayText',
    defaultMessage: "If you're looking for a great deal you can't argue with free, so check out some of the best free to play titles we're fanatical about below",
  },
  blackfriday: {
    id: 'nav.store-menu.blackfriday',
    defaultMessage: 'Black Friday',
  },
  blackfridaytext: {
    id: 'nav.store-menu.blackfridaytext',
    defaultMessage: "Ready for Black Friday's best deals? Discover amazing savings on top games, bundle deals, eBooks, and software right here! Shop now and score big on this year's must haves.",
  },
  xbox: {
    id: 'nav.store-menu.xbox',
    defaultMessage: 'Xbox Games',
  },
  xboxtext: {
    id: 'nav.store-menu.xboxtext',
    defaultMessage: 'Whether you\'re a newcomer or a longtime fan, the Xbox game selection promises endless entertainment and adventure.',
  },
  recommendationsTitle: {
    id: 'user-recommendations.title',
    defaultMessage: 'Recommended for you',
  },
  recommendationsBlurbSteam: {
    id: 'user-recommendations.blurb-steam',
    defaultMessage: 'These recommendations are based on games you have wished or purchased on Fanatical, or own in your Steam Library. Keep adding games to your wishlist to get more relevant results',
  },
  recommendationsBlurbNoSteam: {
    id: 'user-recommendations.blurb-no-steam',
    defaultMessage: 'These recommendations are based on games you have wished or purchased on Fanatical. {syncLink} to get more relevant results',
  },
  recommendationsSyncLink: {
    id: 'user-recommendations.blurb-sync-link',
    defaultMessage: 'Sync your steam library and wishlist with Fanatical',
  },
  recommendationsSmallPrint: {
    id: 'user-recommendations.small-print',
    defaultMessage: 'Personalised recommendations are refreshed once per day, check back for more tomorrow!',
  },
  featuredproduct: {
    id: 'nav.store-menu.featuredproduct',
    defaultMessage: 'Featured Product',
  },
  gamesforyou: {
    id: 'nav.store-menu.gamesforyou',
    defaultMessage: 'Games for You',
  },
});

let hasOpened = false;

function DiscoverMegaMenu({
  intl,
  megaMenuTab,
  setMegaMenuTab,
  starDeal,
  topSellerHits,
  newReleaseHits,
  comingSoonHits,
  endingSoonHits,
  latestDealsHits,
  popularGamesHits,
  gameBundleHits,
  onSaleHits,
  bestOfGamesOf2025,
  freeToPlay,
  featured,
  featuredSecond,
  userLibrary,
  steamLibrary,
  xboxHits,
}) {
  const [tab, setTab] = useState('gamedeals');
  const { formatMessage } = intl;

  if (megaMenuTab === 'discover') {
    hasOpened = true;
  }

  const toggleTab = (newTab) => {
    if (tab !== newTab) {
      setTab(newTab);
    }
  };

  const onClick = () => {
    setMegaMenuTab('');
  };

  // Combine fanatical owned and steam owned
  const purchaseHistoryNames = [...userLibrary, ...steamLibrary];
  const hasFeatured = megaMenuTab === 'discover' && !isEmpty(featured) && !purchaseHistoryNames.includes(featured.name);
  const hasFeaturedSecond = megaMenuTab === 'discover' && !isEmpty(featuredSecond) && !purchaseHistoryNames.includes(featuredSecond.name);


  const isAuthenticated = useSelector(state => !!state.auth?.authenticated);
  const isSteamLinked = useSelector(state => !!state.auth?.steam?.syncSettings?.ownedSync);
  const recommendations = useSelector(state => state.userRecommendations?.recommendations);

  const showRecommendations =
    hasOpened &&
    isAuthenticated &&
    recommendations &&
    recommendations.length;

  // To avoid loading the large megamenu into the DOM, create a simple link structure for each tab below.
  const menuLinks = [
    { name: formatMessage(messages.gamedeals), link: '/on-sale', products: onSaleHits, tabId: 'gamedeals' },
    { name: formatMessage(messages.featuredproduct), product: featured, tabId: 'featured' },
    { name: formatMessage(messages.featuredproduct), product: featuredSecond, tabId: 'featuredSecond' },
    { name: formatMessage(messages.stardeal), product: starDeal, tabId: 'stardeal' },
    { name: formatMessage(messages.gamesforyou), products: recommendations || [], tabId: 'recommendations', new: true },
    { name: formatMessage(messages.gamebundles), link: '/bundle/games', products: gameBundleHits, tabId: 'gamebundles' },
    { name: formatMessage(messages.topsellers), link: '/top-sellers', products: topSellerHits, tabId: 'topsellers' },
    { name: formatMessage(messages.latestdeals), link: '/latest-deals', products: latestDealsHits, tabId: 'latestdeals' },
    { name: formatMessage(messages.trendingdeals), link: '/trending-deals', products: popularGamesHits, tabId: 'trendingdeals' },
    { name: formatMessage(messages.lastchance), link: '/ending-soon', products: endingSoonHits, tabId: 'lastchance' },
    { name: formatMessage(messages.newreleases), link: '/new-releases', products: newReleaseHits, tabId: 'newreleases' },
    { name: formatMessage(messages.upcominggames), link: '/upcoming-games', products: newReleaseHits, tabId: 'upcominggames' },
    { name: formatMessage(messages.xbox), link: '/publishers/xbox', products: xboxHits, tabId: 'xboxgames' },
    { name: formatMessage(messages.bestgamesof2025), link: '/best-games-of-2025', products: bestOfGamesOf2025, tabId: 'bestgamesof2025' },
    { name: formatMessage(messages.freeToPlay), link: '/free-to-play', products: freeToPlay, tabId: 'freeToPlay' },
  ];

  return (
    <div
      role="region"
      aria-labelledby="MegaMenu_discover_link"
      className="container MegaMenuContainer MegaMenuContainer__discover"
      onMouseEnter={() => setMegaMenuTab('discover')}
      onMouseLeave={() => setMegaMenuTab('')}
    >
      <AccessibilityMenu menuLinks={menuLinks} from="discover" />
      {megaMenuTab === 'discover' &&
        <div className={classNames('MegaMenu', { 'MegaMenu--visible': megaMenuTab === 'discover' })}>
          <div className="ProductsMegaMenu">
            <div className="ProductsMegaMenu__tabLinks">
              <ul className="ProductsMegaMenu__linksContainer" role="menu" id="MegaMenu_discover">
                {menuLinks.map((menuLink) => {
                  if (menuLink.tabId === 'xboxgames' && (!xboxAvailable || isEmpty(menuLink.products))) return false;
                  if (menuLink.tabId === 'recommendations' && !showRecommendations) return false;
                  if ((menuLink.tabId === 'featured' || menuLink.tabId === 'featuredSecond' || menuLink.tabId === 'stardeal') && isEmpty(menuLink.product)) return false; // If no product, don't show the tab
                  if ((menuLink.tabId === 'featured' || menuLink.tabId === 'featuredSecond') && purchaseHistoryNames.includes(menuLink?.product?.name)) return false; // Don't show featured products tab if already owned

                  if (menuLink.tabId === 'featured' || menuLink.tabId === 'featuredSecond' || menuLink.tabId === 'stardeal') {
                    let name = menuLink.product.name;
                    if (menuLink.tabId === 'stardeal') name = formatMessage(messages.stardeal);
                    return (
                      <ProductsMegaMenuTabLink key={menuLink.tabId} name={name} link={`/${menuLink.product.type}/${menuLink.product.slug}`} tab={tab} toggle={toggleTab} tabId={menuLink.tabId} from="discover" setMegaMenuTab={setMegaMenuTab} />
                    );
                  }

                  return (
                    <ProductsMegaMenuTabLink key={menuLink.tabId} name={menuLink.name} link={menuLink.link} tab={tab} toggle={toggleTab} tabId={menuLink.tabId} from="discover" setMegaMenuTab={setMegaMenuTab} showNewLabel={menuLink.new} />
                  );
                })}

                <li className="ProductsMegaMenu__link">
                  <Link
                    to="/search"
                    tabIndex={0}
                    role="menuitem"
                    id="discover-browseall"
                    className="ProductsMegaMenu__link__tag"
                    onClick={onClick}
                  >
                    {formatMessage(messages.browseAll)}
                  </Link>
                </li>
              </ul>
            </div>

            <ProductsMegaMenuTab
              showProductLinks
              productLinksCount={6}
              tab={tab}
              title={formatMessage(messages.recommendationsTitle)}
              hits={recommendations}
              setMegaMenuTab={setMegaMenuTab}
              section={'recommendations'}
              subText={formatMessage(messages.recommendationsSmallPrint)}
              viewedHook={() => logViewed(recommendations?.slice(0, 3).map(r => r.slug))}
            >
              <p className="mt-2 mb-0 text-left">
                {isSteamLinked ?
                  formatMessage(messages.recommendationsBlurbSteam)
                  :
                  formatMessage(messages.recommendationsBlurbNoSteam, {
                    syncLink: <Link className="brand-link" to="/account/linked-accounts">{formatMessage(messages.recommendationsSyncLink)}</Link>,
                  })
                }
              </p>
            </ProductsMegaMenuTab>

            <ProductsMegaMenuTab
              showProductLinks
              tab={tab}
              title={formatMessage(messages.gamedeals)}
              hits={onSaleHits}
              setMegaMenuTab={setMegaMenuTab}
              section={'gamedeals'}
              linkTo={'/on-sale'}
              subText={formatMessage(messages.topsellerstext)}
            />

            <ProductsMegaMenuTab
              showProductLinks
              tab={tab}
              title={formatMessage(messages.gamebundles)}
              hits={gameBundleHits}
              setMegaMenuTab={setMegaMenuTab}
              section={'gamebundles'}
              linkTo={'/bundle/games'}
              subText={formatMessage(messages.gamebundlestext)}
            />

            <ProductsMegaMenuTab
              showProductLinks
              tab={tab}
              title={formatMessage(messages.topsellers)}
              hits={topSellerHits}
              setMegaMenuTab={setMegaMenuTab}
              section={'topsellers'}
              linkTo={'/top-sellers'}
              subText={formatMessage(messages.topsellerstext)}
            />

            <ProductsMegaMenuTab
              showProductLinks
              tab={tab}
              title={formatMessage(messages.latestdeals)}
              hits={latestDealsHits}
              setMegaMenuTab={setMegaMenuTab}
              section={'latestdeals'}
              linkTo={'/latest-deals'}
              subText={formatMessage(messages.latestdealstext)}
            />

            <ProductsMegaMenuTab
              showProductLinks
              tab={tab}
              title={formatMessage(messages.trendingdeals)}
              hits={popularGamesHits}
              setMegaMenuTab={setMegaMenuTab}
              section={'trendingdeals'}
              linkTo={'/trending-deals'}
              subText={formatMessage(messages.trendingdealstext)}
            />

            <ProductsMegaMenuTab
              showProductLinks
              tab={tab}
              title={formatMessage(messages.newreleases)}
              hits={newReleaseHits}
              setMegaMenuTab={setMegaMenuTab}
              section={'newreleases'}
              linkTo={'/new-releases'}
              subText={formatMessage(messages.newreleasestext)}
            />

            <ProductsMegaMenuTab
              showProductLinks
              tab={tab}
              title={formatMessage(messages.upcominggames)}
              hits={comingSoonHits}
              setMegaMenuTab={setMegaMenuTab}
              section={'upcominggames'}
              linkTo={'/upcoming-games'}
              subText={formatMessage(messages.upcominggamestext)}
            />

            <ProductsMegaMenuTab
              showProductLinks
              tab={tab}
              title={formatMessage(messages.lastchance)}
              hits={endingSoonHits}
              setMegaMenuTab={setMegaMenuTab}
              section={'lastchance'}
              linkTo={'/ending-soon'}
              subText={formatMessage(messages.lastchancetext)}
            />

            <ProductsMegaMenuTab
              showProductLinks
              tab={tab}
              title={formatMessage(messages.bestgamesof2025)}
              hits={bestOfGamesOf2025}
              setMegaMenuTab={setMegaMenuTab}
              section={'bestgamesof2025'}
              linkTo={'/best-games-of-2025'}
              subText={formatMessage(messages.bestgamesof2025text)}
            />

            <ProductsMegaMenuTab
              tab={tab}
              title={formatMessage(messages.freeToPlay)}
              hits={freeToPlay}
              setMegaMenuTab={setMegaMenuTab}
              section={'freeToPlay'}
              linkTo={'/free-to-play'}
              subText={formatMessage(messages.freeToPlayText)}
            />

            <ProductsMegaMenuTab
              showProductLinks
              tab={tab}
              title={formatMessage(messages.xbox)}
              hits={xboxHits}
              setMegaMenuTab={setMegaMenuTab}
              section={'xboxgames'}
              linkTo={'/publishers/xbox'}
              subText={formatMessage(messages.xboxtext)}
            />

            <StarDealTab
              starDeal={starDeal}
              setMegaMenuTab={setMegaMenuTab}
              tab={tab}
              section={'stardeal'}
            />

            {hasFeatured &&
              <FeaturedTab
                hit={featured}
                setMegaMenuTab={setMegaMenuTab}
                tab={tab}
                section={'featured'}
              />
            }

            {/* Second Featured product for split test 332 */}
            {hasFeaturedSecond &&
              <FeaturedTab
                hit={featuredSecond}
                setMegaMenuTab={setMegaMenuTab}
                tab={tab}
                section={'featuredSecond'}
              />
            }

          </div>
        </div>
      }
    </div>
  );
}

const mapStateToProps = ({ starDeal, userLibrary, steamLibrary }) =>
  ({ starDeal, userLibrary, steamLibrary });

DiscoverMegaMenu.propTypes = {
  megaMenuTab: PropTypes.string,
  setMegaMenuTab: PropTypes.func,
  starDeal: PropTypes.shape({}),
  topSellerHits: PropTypes.arrayOf(PropTypes.object),
  newReleaseHits: PropTypes.arrayOf(PropTypes.object),
  comingSoonHits: PropTypes.arrayOf(PropTypes.object),
  endingSoonHits: PropTypes.arrayOf(PropTypes.object),
  latestDealsHits: PropTypes.arrayOf(PropTypes.object),
  popularGamesHits: PropTypes.arrayOf(PropTypes.object),
  onSaleHits: PropTypes.arrayOf(PropTypes.object),
  gameBundleHits: PropTypes.arrayOf(PropTypes.object),
  xboxHits: PropTypes.arrayOf(PropTypes.object),
  bestOfGamesOf2025: PropTypes.arrayOf(PropTypes.object),
  freeToPlay: PropTypes.arrayOf(PropTypes.object),
  featured: PropTypes.shape({}),
  featuredSecond: PropTypes.shape({}),
  userLibrary: PropTypes.arrayOf(PropTypes.string),
  steamLibrary: PropTypes.arrayOf(PropTypes.string),
};

DiscoverMegaMenu.defaultProps = {
  megaMenuTab: '',
  setMegaMenuTab: () => {},
  starDeal: {},
  topSellerHits: [],
  newReleaseHits: [],
  comingSoonHits: [],
  endingSoonHits: [],
  latestDealsHits: [],
  popularGamesHits: [],
  onSaleHits: [],
  gameBundleHits: [],
  xboxHits: [],
  bestOfGamesOf2025: [],
  freeToPlay: [],
  featured: null,
  featuredSecond: null,
  userLibrary: [],
  steamLibrary: [],
};

export default connect(mapStateToProps)(injectIntl(DiscoverMegaMenu));
